import { msg } from '@lingui/core/macro';
import { commandRegistry, CommandError } from './registry';
import type { LocalSystemContent } from '../types';
import type { CommandContext } from './registry';
import { channelDisplayName } from '../lib/channelDisplay';
import { useMessageStore } from '../stores/useMessageStore';
import { useChannelStore } from '../stores/useChannelStore';
import { useDmStore } from '../stores/useDmStore';
import { useChannelMemberStore } from '../stores/useChannelMemberStore';
import { onChannelLeave } from '../stores/actions';

const commandUsageError = (usage: string) => new CommandError(msg`Usage: ${usage}`);

commandRegistry.register({
  name: 'help',
  description: msg`Show all available commands`,
  usage: '/help',
  paramType: 'none',
  execute: async ({ channelId }: CommandContext) => {
    const groups = commandRegistry.search('');
    const sections: LocalSystemContent = groups.flatMap(group => [
      ['── ', group.kind === 'builtin' ? msg`System` : `🤖 ${group.group}`, ' ──'],
      ...group.items.map(command => [`\`${command.usage}\` — `, command.description]),
      [],
    ]);
    useMessageStore.getState().insertLocalSystemMessage(channelId, sections);
  },
});

commandRegistry.register({
  name: 'leave',
  description: msg`Leave the current channel`,
  usage: '/leave',
  paramType: 'none',
  execute: async ({ channelId, api, i18n }: CommandContext) => {
    const confirmed = window.confirm(i18n._(msg`Leave the current channel?`));
    if (!confirmed) return;
    await api.leaveChannel(channelId);
    onChannelLeave(channelId);
  },
});

commandRegistry.register({
  name: 'topic',
  description: msg`Set the channel topic`,
  usage: '/topic <text>',
  paramType: 'text',
  placeholder: msg`Enter the channel topic…`,
  execute: async ({ channelId, args, api }: CommandContext) => {
    if (!args.trim()) throw commandUsageError('/topic <text>');
    await api.updateChannel(channelId, { topic: args.trim() });
  },
});

commandRegistry.register({
  name: 'invite',
  description: msg`Invite a user to the channel`,
  usage: '/invite @user',
  paramType: 'user',
  placeholder: msg`Select a user…`,
  execute: async ({ channelId, resolvedUser, api }: CommandContext) => {
    if (!resolvedUser) throw commandUsageError('/invite @user');
    await api.addChannelMember(channelId, resolvedUser.id);
  },
});

commandRegistry.register({
  name: 'dm',
  description: msg`Open a direct message with a user`,
  usage: '/dm @user',
  paramType: 'user',
  placeholder: msg`Select a user…`,
  execute: async ({ resolvedUser }: CommandContext) => {
    if (!resolvedUser) throw commandUsageError('/dm @user');
    await useDmStore.getState().openDm(resolvedUser.id);
  },
});

commandRegistry.register({
  name: 'status',
  description: msg`Show channel status`,
  usage: '/status',
  paramType: 'none',
  execute: async ({ channelId, api }: CommandContext) => {
    const roster = await useChannelMemberStore.getState().loadChannelMembers(
      channelId, useChannelStore.getState().channelMembersVersion[channelId] ?? 0,
    );
    if (roster.status === 'unavailable') throw new CommandError(msg`Channel member roster is unavailable. Join the channel to use /status.`);
    const { channel } = await api.getChannel(channelId);
    const members = roster.members;
    const onlineIds = await api.fetchOnlineUsers();
    const onlineSet = new Set(onlineIds);
    const online = members.filter(m => onlineSet.has(m.user_id));
    const offline = members.filter(m => !onlineSet.has(m.user_id));

    const memberCount = members.length;
    const onlineCount = online.length;
    const lines: LocalSystemContent = [
      [`**#${channelDisplayName(channel)}**`],
      [msg`Topic:`, ' ', channel.topic || msg`None`],
      [msg`Members: ${memberCount} (${onlineCount} online)`],
      ...(online.length ? [['🟢 ', msg`Online:`, ' ', online.map(member => member.display_name).join(', ')]] : []),
      ...(offline.length ? [['⚫ ', msg`Offline:`, ' ', offline.map(member => member.display_name).join(', ')]] : []),
    ];
    useMessageStore.getState().insertLocalSystemMessage(channelId, lines);
  },
});

commandRegistry.register({
  name: 'clear',
  description: msg`Clear local chat history`,
  usage: '/clear',
  paramType: 'none',
  execute: async ({ channelId, i18n }: CommandContext) => {
    const confirmed = window.confirm(i18n._(msg`Clear local chat history? This only clears the local display and does not affect server data.`));
    if (!confirmed) return;
    useMessageStore.getState().clearLocalMessages(channelId);
    useMessageStore.getState().insertLocalSystemMessage(channelId, [['🗑️ ', msg`Local chat history cleared`]]);
  },
});

commandRegistry.register({
  name: 'nick',
  description: msg`Change your display name`,
  usage: '/nick <name>',
  paramType: 'text',
  placeholder: msg`New display name…`,
  execute: async ({ channelId, args, api }: CommandContext) => {
    if (!args.trim()) throw commandUsageError('/nick <name>');
    const oldUser = await api.fetchMe();
    const oldName = oldUser.display_name;
    await api.updateProfile({ display_name: args.trim() });
    useMessageStore.getState().insertLocalSystemMessage(channelId, [['✅ ', msg`Display name changed:`, ` ${oldName} → ${args.trim()}`]]);
  },
});
